/**
 * Generated by orval. Do not edit manually.
 */
import * as zod from 'zod'

/**
 * One row per MEMBER, not per signup: someone who left and rejoined holds several signup rows and appears once, represented by their active stint if they have one and their most recent otherwise. `total` counts members on that same basis.

Consequences worth knowing before filtering:
- `status` is that representative stint's, so a rejoined member is `active` and is NOT in the `inactive` / `cancelled` cohorts — those mean 'not a member now'.
- `submitted_at` is when they FIRST joined; `id`, `updated_at` and every signup-payload field describe the representative stint. `stints` carries the rest.
 * @summary List Insider Members
 */
export const listInsiderMembersQuerySearchDefault = ''
export const listInsiderMembersQuerySearchMax = 320

export const listInsiderMembersQueryPokerFormatsDefault = []
export const listInsiderMembersQueryStakesDefault = []
export const listInsiderMembersQuerySolverConfidenceDefault = []
export const listInsiderMembersQueryYearsMinMinOne = 0
export const listInsiderMembersQueryYearsMinMaxOne = 80

export const listInsiderMembersQueryYearsMaxMinOne = 0
export const listInsiderMembersQueryYearsMaxMaxOne = 80

export const listInsiderMembersQueryTracksDefault = []
export const listInsiderMembersQueryAppUsageDefault = []
export const listInsiderMembersQueryAppUsageMissingDefault = false
export const listInsiderMembersQuerySubscriptionTierDefault = []
export const listInsiderMembersQueryNoSubscriptionDefault = false
export const listInsiderMembersQueryLimitDefault = 100
export const listInsiderMembersQueryLimitMax = 200

export const listInsiderMembersQueryOffsetDefault = 0
export const listInsiderMembersQueryOffsetMin = 0

export const listInsiderMembersQueryOrderingDefault = []

export const listInsiderMembersQueryParams = zod.object({
  status: zod
    .union([zod.enum(['active', 'inactive', 'cancelled']), zod.null()])
    .optional()
    .describe('Restrict to one lifecycle status. Omit to list active and former members together.'),
  search: zod
    .string()
    .max(listInsiderMembersQuerySearchMax)
    .optional()
    .describe("Case-insensitive substring match on the member's account email."),
  poker_formats: zod
    .array(zod.enum(['nlhe_cash', 'nlhe_mtt', 'nlhe_sng', 'plo_cash', 'plo_mtt', 'other']))
    .default(listInsiderMembersQueryPokerFormatsDefault)
    .describe(
      'Keep members who play ANY of these formats. Repeat the parameter per value. Values within one facet are OR-ed; separate facets are AND-ed together.',
    ),
  stakes: zod
    .array(zod.enum(['low', 'mid', 'high', 'nosebleeds']))
    .default(listInsiderMembersQueryStakesDefault)
    .describe('Keep members whose primary stake level is ANY of these.'),
  solver_confidence: zod
    .array(zod.enum(['never', 'tried', 'occasional', 'regular', 'extensive']))
    .default(listInsiderMembersQuerySolverConfidenceDefault)
    .describe('Keep members whose self-reported solver experience is ANY of these.'),
  years_min: zod
    .union([
      zod
        .number()
        .min(listInsiderMembersQueryYearsMinMinOne)
        .max(listInsiderMembersQueryYearsMinMaxOne),
      zod.null(),
    ])
    .optional()
    .describe(
      'Inclusive lower bound on `years_with_tools`, compared numerically. Must be <= `years_max`.',
    ),
  years_max: zod
    .union([
      zod
        .number()
        .min(listInsiderMembersQueryYearsMaxMinOne)
        .max(listInsiderMembersQueryYearsMaxMaxOne),
      zod.null(),
    ])
    .optional()
    .describe(
      'Inclusive upper bound on `years_with_tools`. Must be >= `years_min`. Omit for an open-ended bucket.',
    ),
  tracks: zod
    .array(zod.enum(['feature_testing', 'research_calls']))
    .default(listInsiderMembersQueryTracksDefault)
    .describe('Keep members opted into ANY of these tracks.'),
  app_usage: zod
    .array(
      zod.enum([
        'solutions',
        'trainer_drills',
        'board_reports',
        'analyzer',
        'pokerarena',
        'new_here',
      ]),
    )
    .default(listInsiderMembersQueryAppUsageDefault)
    .describe('Keep members who reported ANY of these ways of using GTO Wizard.'),
  app_usage_missing: zod
    .boolean()
    .optional()
    .describe(
      'Also keep members who were never asked the app-usage question — signups predating the field. OR-ed into the `app_usage` facet rather than replacing it, so both can be asked at once. It is a parameter of its own because it is not a value the enum can carry.',
    ),
  subscription_tier: zod
    .array(zod.enum(['FREE', 'PREMIUM', 'ELITE', 'TRIAL', 'STARTER', 'ULTRA']))
    .default(listInsiderMembersQuerySubscriptionTierDefault)
    .describe(
      'Keep members whose CURRENT tier is ANY of these — the tier the row shows, not any subscription they hold. Someone on FREE and PREMIUM at once reads PREMIUM and is therefore NOT in the FREE cohort.',
    ),
  no_subscription: zod
    .boolean()
    .optional()
    .describe(
      'Also keep members with no active subscription, the population `subscription_tier` renders null. OR-ed into the tier facet rather than replacing it, so both can be asked at once. It is a parameter of its own because it is not a value the enum can carry.',
    ),
  limit: zod
    .number()
    .min(1)
    .max(listInsiderMembersQueryLimitMax)
    .default(listInsiderMembersQueryLimitDefault)
    .describe('Members per page.'),
  offset: zod.number().min(listInsiderMembersQueryOffsetMin).optional(),
  ordering: zod
    .array(
      zod.enum([
        'submitted_at',
        '-submitted_at',
        'updated_at',
        '-updated_at',
        'email',
        '-email',
        'status',
        '-status',
        'last_login',
        '-last_login',
        'date_joined',
        '-date_joined',
      ]),
    )
    .default(listInsiderMembersQueryOrderingDefault)
    .describe(
      'Sort keys, applied in order; a `-` prefix sorts descending. Ties break on newest signup then row id, so paging is stable. Defaults to newest signup first. `submitted_at` sorts on when the member FIRST joined, matching the column.',
    ),
})

export const listInsiderMembersResponse = zod.object({
  items: zod.array(
    zod.object({
      id: zod
        .string()
        .describe(
          'Signup row UUID; the path parameter of every member endpoint. One row is one MEMBER, so for someone who left and rejoined this is their representative stint — the active one if they have one, else their most recent. The drill-down shows that stint.',
        ),
      user_id: zod
        .number()
        .describe('Account id — the key for Django admin and the features surface.'),
      email: zod
        .string()
        .describe(
          "Account email, and what `search` matches. The signup's own `email` on the detail response is what the member typed on the form and may differ.",
        ),
      status: zod.enum(['active', 'inactive', 'cancelled']),
      stint_count: zod
        .number()
        .describe(
          'How many separate times this member has signed up (`type=insider` rows). `1` for almost everyone — a value above 1 is a member who left and came back.',
        ),
      rejoined: zod
        .boolean()
        .describe(
          '`stint_count > 1`, resolved here rather than inferred client-side so the table and the CSV cannot disagree about who counts as rejoined.',
        ),
      first_joined_at: zod.iso
        .datetime({})
        .describe(
          "Earliest stint's start. Equal to `submitted_at` by construction — it exists under its own name so the history block does not have to rely on what `submitted_at` means.",
        ),
      stints: zod
        .array(
          zod.object({
            id: zod
              .string()
              .describe(
                "Signup row UUID of this stint. Only the representative stint's id — the one the list row carries — is accepted by the member endpoints; the others are history.",
              ),
            status: zod.enum(['active', 'inactive', 'cancelled']),
            created_at: zod.iso.datetime({}).describe('When the member joined for this stint.'),
            updated_at: zod.iso.datetime({}).describe("When this stint's signup was last edited."),
          }),
        )
        .describe(
          'Every stint this member has held, newest first, the representative one included. A single-element list for the common case, never empty.',
        ),
      tracks: zod
        .array(zod.enum(['feature_testing', 'research_calls']))
        .describe(
          'Tracks the member opted into. Empty is a legal state for an active member, not an error.',
        ),
      features_on: zod
        .number()
        .describe(
          'How many Insider-manageable features currently resolve ON for this member. A resolved figure, not a count of override rows: a deny outranks a grant, and a default-on baseline counts without any row existing.',
        ),
      features_blocked: zod
        .number()
        .describe(
          'How many features a staff member has blocked for them; blocked features are not counted on.',
        ),
      submitted_at: zod.iso
        .datetime({})
        .describe(
          'When this member FIRST joined the programme, so a rejoin does not reset their seniority. The default sort key. For a rejoined member it therefore predates the representative stint that `id`, `status` and `updated_at` describe.',
        ),
      updated_at: zod.iso
        .datetime({})
        .describe('When the member last edited their signup. A staff note edit does not move it.'),
      last_login: zod
        .union([zod.iso.datetime({}), zod.null()])
        .optional()
        .describe(
          "Account's last sign-in. Null for a member who has never logged in since signing up.",
        ),
      date_joined: zod.iso
        .datetime({})
        .describe(
          'When the account was created — not when the member joined Insider (that is `submitted_at`).',
        ),
      discord_tag: zod
        .union([zod.string(), zod.null()])
        .optional()
        .describe(
          "Discord identity from the member's OAuth login. The signup's own `discord_handle` on the detail response is self-typed and can disagree with this one.",
        ),
      language: zod
        .string()
        .describe('Account language (ISO 639-1). Empty when the account never set one.'),
      marketing_opted_out: zod
        .boolean()
        .describe(
          'True when the account opted out of marketing email; a cohort for a campaign must respect it.',
        ),
      is_soft_banned: zod
        .union([zod.boolean(), zod.null()])
        .optional()
        .describe(
          "Whether the account is soft-banned. Null on accounts predating the column's default.",
        ),
      subscription_tier: zod
        .union([zod.enum(['FREE', 'PREMIUM', 'ELITE', 'TRIAL', 'STARTER', 'ULTRA']), zod.null()])
        .optional()
        .describe(
          'Tier the member is subscribed to RIGHT NOW; null when nothing is active. Overlapping subscriptions are normal — every account carries a FREE one — so this is the highest of them by FREE < TRIAL < STARTER < PREMIUM < ELITE < ULTRA. Deliberately NOT sortable: the values sort alphabetically, which reads like a rank and is not one.',
        ),
      subscription_expires_at: zod
        .union([zod.iso.datetime({}), zod.null()])
        .optional()
        .describe(
          'When that subscription lapses, so an imminent lapse is visible. Null alongside a null tier.',
        ),
      subscription_canceled: zod
        .boolean()
        .describe(
          'The member cancelled and is running out the paid period — a subscriber today, not one after `subscription_expires_at`. Also false when there is no subscription at all, so read it beside `subscription_tier`.',
        ),
      name: zod
        .union([zod.string(), zod.null()])
        .optional()
        .describe('Full name as typed on the signup form. The account carries no name of its own.'),
      signup_email: zod
        .union([zod.string(), zod.null()])
        .optional()
        .describe(
          'Email the member typed on the signup form. Not `email`, which is the ACCOUNT address and the one `search` matches.',
        ),
      alternative_email: zod
        .union([zod.string(), zod.null()])
        .optional()
        .describe('Secondary email offered at signup. Null when the member gave none.'),
      discord_handle: zod
        .union([zod.string(), zod.null()])
        .optional()
        .describe(
          "Discord handle as self-typed at signup, so it can disagree with `discord_tag`, which the account's OAuth login supplies.",
        ),
      poker_formats: zod
        .array(zod.enum(['nlhe_cash', 'nlhe_mtt', 'nlhe_sng', 'plo_cash', 'plo_mtt', 'other']))
        .describe(
          'Formats the member reported playing. The `poker_formats` filter selects on this.',
        ),
      poker_formats_other: zod
        .union([zod.string(), zod.null()])
        .optional()
        .describe(
          'Free text typed alongside the `other` format. Null unless the member chose `other`.',
        ),
      stakes: zod
        .union([zod.enum(['low', 'mid', 'high', 'nosebleeds']), zod.null()])
        .optional()
        .describe('Primary stake level the member reported. The `stakes` filter selects on this.'),
      solver_confidence: zod
        .union([zod.enum(['never', 'tried', 'occasional', 'regular', 'extensive']), zod.null()])
        .optional()
        .describe(
          'Self-reported solver experience. The `solver_confidence` filter selects on this.',
        ),
      years_with_tools: zod
        .union([zod.number(), zod.null()])
        .optional()
        .describe(
          'Years using poker training tools. `years_min` / `years_max` bound this numerically.',
        ),
      app_usage: zod
        .array(
          zod.enum([
            'solutions',
            'trainer_drills',
            'board_reports',
            'analyzer',
            'pokerarena',
            'new_here',
          ]),
        )
        .describe(
          'Ways the member reported using GTO Wizard. Empty does not mean they chose nothing — the form requires at least one — it marks a signup predating the question, the population `app_usage_missing=true` selects.',
        ),
      consent_given: zod
        .union([zod.boolean(), zod.null()])
        .optional()
        .describe(
          'Data-processing consent. Both signup requests type it `Literal[True]`, so a stored signup can only ever say true.',
        ),
    }),
  ),
  total: zod.number().describe('Total number of records'),
  limit: zod.number().describe('Limit of records in current page'),
  offset: zod.number().describe('Offset of current page. Records are ordered by date_created'),
})

/**
 * Export the same cohort the members list selects for the same FILTER parameters, as CSV. Deliberately unpaginated — the list's page cap exists only to bound the per-page feature-count walk, which the export does not do, so inheriting it would truncate the file mid-cohort. `ordering`, `limit` and `offset` are ignored if sent: rows are always newest signup first, and a spreadsheet re-sorts. Returns 422 when the cohort exceeds the export ceiling — the file is never silently truncated.

**Response (200):** raw response managed by the handler — not a JSON envelope.
- `Content-Type`: `text/csv`
- `Content-Disposition`: `attachment; filename="insider-members-<date>.csv"`
 * @summary Export Insider Members
 */
export const exportInsiderMembersQuerySearchDefault = ''
export const exportInsiderMembersQuerySearchMax = 320

export const exportInsiderMembersQueryPokerFormatsDefault = []
export const exportInsiderMembersQueryStakesDefault = []
export const exportInsiderMembersQuerySolverConfidenceDefault = []
export const exportInsiderMembersQueryYearsMinMinOne = 0
export const exportInsiderMembersQueryYearsMinMaxOne = 80

export const exportInsiderMembersQueryYearsMaxMinOne = 0
export const exportInsiderMembersQueryYearsMaxMaxOne = 80

export const exportInsiderMembersQueryTracksDefault = []
export const exportInsiderMembersQueryAppUsageDefault = []
export const exportInsiderMembersQueryAppUsageMissingDefault = false
export const exportInsiderMembersQuerySubscriptionTierDefault = []
export const exportInsiderMembersQueryNoSubscriptionDefault = false

export const exportInsiderMembersQueryParams = zod.object({
  status: zod
    .union([zod.enum(['active', 'inactive', 'cancelled']), zod.null()])
    .optional()
    .describe('Restrict to one lifecycle status. Omit to list active and former members together.'),
  search: zod
    .string()
    .max(exportInsiderMembersQuerySearchMax)
    .optional()
    .describe("Case-insensitive substring match on the member's account email."),
  poker_formats: zod
    .array(zod.enum(['nlhe_cash', 'nlhe_mtt', 'nlhe_sng', 'plo_cash', 'plo_mtt', 'other']))
    .default(exportInsiderMembersQueryPokerFormatsDefault)
    .describe(
      'Keep members who play ANY of these formats. Repeat the parameter per value. Values within one facet are OR-ed; separate facets are AND-ed together.',
    ),
  stakes: zod
    .array(zod.enum(['low', 'mid', 'high', 'nosebleeds']))
    .default(exportInsiderMembersQueryStakesDefault)
    .describe('Keep members whose primary stake level is ANY of these.'),
  solver_confidence: zod
    .array(zod.enum(['never', 'tried', 'occasional', 'regular', 'extensive']))
    .default(exportInsiderMembersQuerySolverConfidenceDefault)
    .describe('Keep members whose self-reported solver experience is ANY of these.'),
  years_min: zod
    .union([
      zod
        .number()
        .min(exportInsiderMembersQueryYearsMinMinOne)
        .max(exportInsiderMembersQueryYearsMinMaxOne),
      zod.null(),
    ])
    .optional()
    .describe(
      'Inclusive lower bound on `years_with_tools`, compared numerically. Must be <= `years_max`.',
    ),
  years_max: zod
    .union([
      zod
        .number()
        .min(exportInsiderMembersQueryYearsMaxMinOne)
        .max(exportInsiderMembersQueryYearsMaxMaxOne),
      zod.null(),
    ])
    .optional()
    .describe(
      'Inclusive upper bound on `years_with_tools`. Must be >= `years_min`. Omit for an open-ended bucket.',
    ),
  tracks: zod
    .array(zod.enum(['feature_testing', 'research_calls']))
    .default(exportInsiderMembersQueryTracksDefault)
    .describe('Keep members opted into ANY of these tracks.'),
  app_usage: zod
    .array(
      zod.enum([
        'solutions',
        'trainer_drills',
        'board_reports',
        'analyzer',
        'pokerarena',
        'new_here',
      ]),
    )
    .default(exportInsiderMembersQueryAppUsageDefault)
    .describe('Keep members who reported ANY of these ways of using GTO Wizard.'),
  app_usage_missing: zod
    .boolean()
    .optional()
    .describe(
      'Also keep members who were never asked the app-usage question — signups predating the field. OR-ed into the `app_usage` facet rather than replacing it, so both can be asked at once. It is a parameter of its own because it is not a value the enum can carry.',
    ),
  subscription_tier: zod
    .array(zod.enum(['FREE', 'PREMIUM', 'ELITE', 'TRIAL', 'STARTER', 'ULTRA']))
    .default(exportInsiderMembersQuerySubscriptionTierDefault)
    .describe(
      'Keep members whose CURRENT tier is ANY of these — the tier the row shows, not any subscription they hold. Someone on FREE and PREMIUM at once reads PREMIUM and is therefore NOT in the FREE cohort.',
    ),
  no_subscription: zod
    .boolean()
    .optional()
    .describe(
      'Also keep members with no active subscription, the population `subscription_tier` renders null. OR-ed into the tier facet rather than replacing it, so both can be asked at once. It is a parameter of its own because it is not a value the enum can carry.',
    ),
})

/**
 * @summary Get Insider Member
 */
export const getInsiderMemberParams = zod.object({
  member_id: zod.uuid(),
})

export const getInsiderMemberResponsePokerFormatsOtherMaxOne = 200

export const getInsiderMemberResponse = zod.object({
  id: zod.string().describe('UUID of the insider signup record.'),
  status: zod.enum(['active', 'inactive', 'cancelled']),
  submitted_at: zod.iso.datetime({}).describe('Timestamp when the signup was first submitted.'),
  updated_at: zod.iso
    .datetime({})
    .describe('When the member last edited their signup. A staff note edit does not move it.'),
  name: zod.string().describe("Applicant's full name."),
  email: zod.string().describe('Primary contact email address.'),
  alternative_email: zod
    .union([zod.string(), zod.null()])
    .optional()
    .describe('Optional secondary email.'),
  discord_handle: zod
    .union([zod.string(), zod.null()])
    .optional()
    .describe('Discord handle if provided at submission time.'),
  poker_formats: zod
    .array(zod.enum(['nlhe_cash', 'nlhe_mtt', 'nlhe_sng', 'plo_cash', 'plo_mtt', 'other']))
    .describe('Poker formats the applicant plays.'),
  poker_formats_other: zod
    .union([zod.string().max(getInsiderMemberResponsePokerFormatsOtherMaxOne), zod.null()])
    .optional()
    .describe('Optional free-text poker format supplied by the applicant.'),
  stakes: zod.enum(['low', 'mid', 'high', 'nosebleeds']),
  solver_confidence: zod.enum(['never', 'tried', 'occasional', 'regular', 'extensive']),
  years_with_tools: zod.number().describe('Years of experience using poker training tools.'),
  tracks: zod
    .array(zod.enum(['feature_testing', 'research_calls']))
    .describe('Insider tracks the applicant joined.'),
  app_usage: zod
    .array(
      zod.enum([
        'solutions',
        'trainer_drills',
        'board_reports',
        'analyzer',
        'pokerarena',
        'new_here',
      ]),
    )
    .optional()
    .describe(
      'Ways the applicant uses GTO Wizard. Empty for legacy submissions made before this field existed.',
    ),
  consent_given: zod.boolean().describe('True if the applicant consented to data processing.'),
  user_id: zod.number().describe('Account id — the key for Django admin and the features surface.'),
  account_email: zod
    .string()
    .describe(
      'Account email, the value the members list shows. `email` above is what the member typed.',
    ),
  note: zod
    .string()
    .describe("The manager's private per-member scratchpad. The only writable field."),
  subscription_tier: zod
    .union([zod.enum(['FREE', 'PREMIUM', 'ELITE', 'TRIAL', 'STARTER', 'ULTRA']), zod.null()])
    .optional()
    .describe(
      'Tier the member is subscribed to right now, resolved exactly as on the members list.',
    ),
  subscription_expires_at: zod
    .union([zod.iso.datetime({}), zod.null()])
    .optional()
    .describe('When that subscription lapses. Null alongside a null tier.'),
  subscription_canceled: zod
    .boolean()
    .describe(
      'The member cancelled and is running out the paid period. Also false when nothing is active.',
    ),
})

/**
 * @summary Update Insider Member
 */
export const updateInsiderMemberParams = zod.object({
  member_id: zod.uuid(),
})

export const updateInsiderMemberBodyNoteMax = 2000

export const updateInsiderMemberBody = zod.object({
  note: zod
    .string()
    .max(updateInsiderMemberBodyNoteMax)
    .describe('Replaces the whole note. Send an empty string to clear it.'),
})

export const updateInsiderMemberResponsePokerFormatsOtherMaxOne = 200

export const updateInsiderMemberResponse = zod.object({
  id: zod.string().describe('UUID of the insider signup record.'),
  status: zod.enum(['active', 'inactive', 'cancelled']),
  submitted_at: zod.iso.datetime({}).describe('Timestamp when the signup was first submitted.'),
  updated_at: zod.iso
    .datetime({})
    .describe('When the member last edited their signup. A staff note edit does not move it.'),
  name: zod.string().describe("Applicant's full name."),
  email: zod.string().describe('Primary contact email address.'),
  alternative_email: zod
    .union([zod.string(), zod.null()])
    .optional()
    .describe('Optional secondary email.'),
  discord_handle: zod
    .union([zod.string(), zod.null()])
    .optional()
    .describe('Discord handle if provided at submission time.'),
  poker_formats: zod
    .array(zod.enum(['nlhe_cash', 'nlhe_mtt', 'nlhe_sng', 'plo_cash', 'plo_mtt', 'other']))
    .describe('Poker formats the applicant plays.'),
  poker_formats_other: zod
    .union([zod.string().max(updateInsiderMemberResponsePokerFormatsOtherMaxOne), zod.null()])
    .optional()
    .describe('Optional free-text poker format supplied by the applicant.'),
  stakes: zod.enum(['low', 'mid', 'high', 'nosebleeds']),
  solver_confidence: zod.enum(['never', 'tried', 'occasional', 'regular', 'extensive']),
  years_with_tools: zod.number().describe('Years of experience using poker training tools.'),
  tracks: zod
    .array(zod.enum(['feature_testing', 'research_calls']))
    .describe('Insider tracks the applicant joined.'),
  app_usage: zod
    .array(
      zod.enum([
        'solutions',
        'trainer_drills',
        'board_reports',
        'analyzer',
        'pokerarena',
        'new_here',
      ]),
    )
    .optional()
    .describe(
      'Ways the applicant uses GTO Wizard. Empty for legacy submissions made before this field existed.',
    ),
  consent_given: zod.boolean().describe('True if the applicant consented to data processing.'),
  user_id: zod.number().describe('Account id — the key for Django admin and the features surface.'),
  account_email: zod
    .string()
    .describe(
      'Account email, the value the members list shows. `email` above is what the member typed.',
    ),
  note: zod
    .string()
    .describe("The manager's private per-member scratchpad. The only writable field."),
  subscription_tier: zod
    .union([zod.enum(['FREE', 'PREMIUM', 'ELITE', 'TRIAL', 'STARTER', 'ULTRA']), zod.null()])
    .optional()
    .describe(
      'Tier the member is subscribed to right now, resolved exactly as on the members list.',
    ),
  subscription_expires_at: zod
    .union([zod.iso.datetime({}), zod.null()])
    .optional()
    .describe('When that subscription lapses. Null alongside a null tier.'),
  subscription_canceled: zod
    .boolean()
    .describe(
      'The member cancelled and is running out the paid period. Also false when nothing is active.',
    ),
})

/**
 * Every published Insider-manageable feature, resolved for this member: the exposure baseline, the staff record and the member's own record kept structurally separate, and the state the pair resolves to.

Deliberately unpaginated — the published set is small and curated by hand, and an envelope here would be ceremony the caller has to unwind before it can render anything.
 * @summary List Insider Member Features
 */
export const listInsiderMemberFeaturesParams = zod.object({
  member_id: zod.uuid(),
})

export const listInsiderMemberFeaturesResponseItem = zod
  .object({
    exposure: zod.enum(['hidden', 'offered', 'default_on']),
    staff_state: zod
      .union([zod.enum(['granted', 'denied']), zod.null()])
      .optional()
      .describe('State of the staff record, or null when there is none.'),
    self_state: zod
      .union([zod.enum(['granted', 'denied']), zod.null()])
      .optional()
      .describe("State of the member's own record, or null when there is none."),
    staff_record: zod
      .union([
        zod.object({
          actor_id: zod
            .union([zod.number(), zod.null()])
            .optional()
            .describe(
              'Who wrote the record. Null on a backfilled row — a first-class value, not an error.',
            ),
          actor_email: zod
            .union([zod.string(), zod.null()])
            .optional()
            .describe('Email of `actor_id`, or null when there is no actor.'),
          note: zod
            .string()
            .describe('Free-text note captured when the record was written; empty is normal.'),
          created_at: zod.iso.datetime({}).describe('When this record was first written.'),
          updated_at: zod.iso.datetime({}).describe('When this record was last changed.'),
        }),
        zod.null(),
      ])
      .optional()
      .describe(
        "Provenance of the staff record. Structurally separate from `self_record`: they carry different actors and timestamps, and no staff action ever rewrites the member's own row.",
      ),
    self_record: zod
      .union([
        zod.object({
          actor_id: zod
            .union([zod.number(), zod.null()])
            .optional()
            .describe(
              'Who wrote the record. Null on a backfilled row — a first-class value, not an error.',
            ),
          actor_email: zod
            .union([zod.string(), zod.null()])
            .optional()
            .describe('Email of `actor_id`, or null when there is no actor.'),
          note: zod
            .string()
            .describe('Free-text note captured when the record was written; empty is normal.'),
          created_at: zod.iso.datetime({}).describe('When this record was first written.'),
          updated_at: zod.iso.datetime({}).describe('When this record was last changed.'),
        }),
        zod.null(),
      ])
      .optional()
      .describe("Provenance of the member's own record. No admin action writes or deletes it."),
    effective: zod
      .boolean()
      .describe(
        "Whether the Insider model resolves this feature ON for the account: any deny wins, then any grant, then the exposure baseline. Waffle's own rungs are not a term.",
      ),
    state: zod.enum([
      'blocked',
      'granted',
      'granted_opted_out',
      'opted_in',
      'on_by_default',
      'off_by_default',
      'offered_not_taken',
      'not_offered',
      'not_a_member',
      'switched_off_by_member',
      'feature_testing_off',
    ]),
    state_label: zod.string().describe("Short label for `state`, in the manager's words."),
    charter_row: zod
      .union([zod.number(), zod.null()])
      .optional()
      .describe('Row number in the nine-row override table, or null for the tenth state it omits.'),
    explanation: zod.string().describe('One sentence naming who put the pair in this state.'),
    vetoed_by_member: zod
      .boolean()
      .describe(
        "True when a member's own deny is what holds the feature off. No staff action can clear it, so a grant offered here will not switch the feature on.",
      ),
    legal_actions: zod
      .array(zod.enum(['grant', 'revoke', 'block', 'unblock']))
      .describe(
        'Staff actions that change something in this state. An action absent here is a no-op, not a permission failure.',
      ),
    flag_id: zod.number().describe('Waffle flag id; the path parameter of every feature endpoint.'),
    name: zod.string().describe('Waffle flag name — the code contract, not editable here.'),
    public_name: zod.string().describe('User-visible name; empty means the FE humanizes `name`.'),
    description: zod.string().describe('User-visible description. English-only for v1.'),
  })
  .describe(
    'One published feature, resolved for one member — the transpose of `InsiderFeatureUserSchema`.',
  )
export const listInsiderMemberFeaturesResponse = zod.array(listInsiderMemberFeaturesResponseItem)

/**
 * @summary End Insider Membership
 */
export const endInsiderMembershipParams = zod.object({
  member_id: zod.uuid(),
})

export const endInsiderMembershipQueryRevokeGrantsDefault = false

export const endInsiderMembershipQueryParams = zod.object({
  revoke_grants: zod
    .boolean()
    .optional()
    .describe(
      "Also delete every staff grant (`staff/granted`) this member holds, in the same transaction that ends the membership. The member's own records are untouched, so re-joining restores their choices, and staff blocks survive — a block is not a grant.",
    ),
})

export const endInsiderMembershipResponsePokerFormatsOtherMaxOne = 200

export const endInsiderMembershipResponse = zod.object({
  id: zod.string().describe('UUID of the insider signup record.'),
  status: zod.enum(['active', 'inactive', 'cancelled']),
  submitted_at: zod.iso.datetime({}).describe('Timestamp when the signup was first submitted.'),
  updated_at: zod.iso
    .datetime({})
    .describe('When the member last edited their signup. A staff note edit does not move it.'),
  name: zod.string().describe("Applicant's full name."),
  email: zod.string().describe('Primary contact email address.'),
  alternative_email: zod
    .union([zod.string(), zod.null()])
    .optional()
    .describe('Optional secondary email.'),
  discord_handle: zod
    .union([zod.string(), zod.null()])
    .optional()
    .describe('Discord handle if provided at submission time.'),
  poker_formats: zod
    .array(zod.enum(['nlhe_cash', 'nlhe_mtt', 'nlhe_sng', 'plo_cash', 'plo_mtt', 'other']))
    .describe('Poker formats the applicant plays.'),
  poker_formats_other: zod
    .union([zod.string().max(endInsiderMembershipResponsePokerFormatsOtherMaxOne), zod.null()])
    .optional()
    .describe('Optional free-text poker format supplied by the applicant.'),
  stakes: zod.enum(['low', 'mid', 'high', 'nosebleeds']),
  solver_confidence: zod.enum(['never', 'tried', 'occasional', 'regular', 'extensive']),
  years_with_tools: zod.number().describe('Years of experience using poker training tools.'),
  tracks: zod
    .array(zod.enum(['feature_testing', 'research_calls']))
    .describe('Insider tracks the applicant joined.'),
  app_usage: zod
    .array(
      zod.enum([
        'solutions',
        'trainer_drills',
        'board_reports',
        'analyzer',
        'pokerarena',
        'new_here',
      ]),
    )
    .optional()
    .describe(
      'Ways the applicant uses GTO Wizard. Empty for legacy submissions made before this field existed.',
    ),
  consent_given: zod.boolean().describe('True if the applicant consented to data processing.'),
  user_id: zod.number().describe('Account id — the key for Django admin and the features surface.'),
  account_email: zod
    .string()
    .describe(
      'Account email, the value the members list shows. `email` above is what the member typed.',
    ),
  note: zod
    .string()
    .describe("The manager's private per-member scratchpad. The only writable field."),
  subscription_tier: zod
    .union([zod.enum(['FREE', 'PREMIUM', 'ELITE', 'TRIAL', 'STARTER', 'ULTRA']), zod.null()])
    .optional()
    .describe(
      'Tier the member is subscribed to right now, resolved exactly as on the members list.',
    ),
  subscription_expires_at: zod
    .union([zod.iso.datetime({}), zod.null()])
    .optional()
    .describe('When that subscription lapses. Null alongside a null tier.'),
  subscription_canceled: zod
    .boolean()
    .describe(
      'The member cancelled and is running out the paid period. Also false when nothing is active.',
    ),
})
